import './EditTableView.scss';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { Dispatch, ReactElement, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdOpenInNew, MdWarning } from 'react-icons/md';
import {
  formatCAD,
  formatMoney,
  formatNumber,
  formatNumberAsInt
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  AVAILS,
  LENGTHS,
  PlanRow,
  RowEdit,
  TYPES,
  TYPES_TO_NAMES
} from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { Button } from '@Components/Button';
import { Appearance, Intent, Placement, Size } from '@Components/designConstants';
import { Dropdown, DropdownOption, DropdownVariant, MenuItem } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { SelfFocusingInput } from '@Components/SelfFocusingElements';
import { Spinner } from '@Components/Spinner';
import { BPMTable, CellRenderer } from '@Components/StickyTable';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import { CURRENT_WEEK_START, DAYS_OF_WEEK } from '@src/utils/date-utils';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { convert24hrTo12hr } from '@src/utils/time-utils';
import {
  ABBREVIATED_MEAUREMENT_NAMES,
  MEDIA_CLASSIFICATION_OPTIONS,
  ORDER_TYPE_OPTIONS,
  PRETTY_DEMO_NAMES,
  TYPE_TO_STATUS_IN_NETWORK_PRICING
} from './linear-buying-constants';
import {
  calculateAudienceInfo,
  CampaignMetadata,
  createNewRowFromSpots,
  deepMergeLinearImpressionOverrides,
  EditsMap,
  findOldMetricNumber,
  findOldMetricString,
  FormattedRow,
  getFormattedRow,
  isMultiDatePicker,
  isMultiWeekEdited,
  isNew,
  isNewRowInAggregate,
  NielsenEstimates,
  RotationPricing,
  RotationsAndPricing,
  time24hr,
  weekFormatter
} from './linear-buying-utils';
import { areAllRowsCovered } from './blanket-traffic-utils';
import { AddOrderedImpressionsModal } from './Modals/AddOrderedImpressionsModal';
import { OverrideRotationModal } from './Modals/OverrideRotationModal';

interface FormattedRowTableType {
  count: number | string;
  cpm: number | string;
  impressions: number | string;
  totalSpend: number | string;
  trp: number | string;
}

type totalsRowAggSpots = Record<string, number>;

const renderTotals: CellRenderer<string | number | Element | undefined> = ({
  data,
  style = {},
  classes = []
}) => (
  <div style={style} className={cn(classes, 'grand-total-cell')}>
    {data instanceof Element ? data.textContent : data}
  </div>
);

interface BlanketCoveredTooltipProps {
  covered: boolean;
  rowKey: string;
  children: ReactElement;
}

/**
 * Explains the disabled creative editor on a row a blanket instruction covers. Only wraps when
 * covered -- a disabled control needs the reason, an enabled one needs no tooltip, and the span
 * is there because a disabled button emits no pointer events for the trigger to hear.
 */
const BlanketCoveredTooltip = ({ covered, rowKey, children }: BlanketCoveredTooltipProps) => {
  if (!covered) {
    return children;
  }
  return (
    <OverlayTrigger
      placement={Placement.LEFT_START}
      overlay={
        <Tooltip id={`covered-${rowKey}`}>
          Covered by a blanket traffic instruction. Change the pool from Blanket Traffic
          Instructions in the page menu.
        </Tooltip>
      }
    >
      <span>{children}</span>
    </OverlayTrigger>
  );
};

const renderNoRows = () => (
  <div className='no-rows'>
    There are no orders for the selected week(s). Start by adding a row or importing a buy.
  </div>
);

const isUpfront = (type: number) => TYPES_TO_NAMES[type] === 'Upfront';

/**
 * Use this when changing Rotation or Type to get new values from Rotations and Pricing.
 * If type is general, use either general or buyable. Otherwise, we want them to match.
 */
const shouldUseRotation = (
  formattedType: 'buyable' | 'secured' | 'general' | 'remnant' | 'upfront',
  status: string
) => {
  if (formattedType === 'general') {
    return formattedType === status || status === 'buyable';
  }
  return formattedType === status;
};

/**
 * Checks which type options to show for a given network.
 * TODO: change this once we add type to linear rates.
 */
const getTypeOptions = (
  networkPricing: Record<string, RotationPricing[]>,
  enableUpfrontLinear
): MenuItem<string>[] => {
  const typesToUse = {
    Regular: false,
    Secured: false,
    Remnant: false,
    Upfront: enableUpfrontLinear,
    // Always show General type as an option.
    General: true
  };

  for (const avail of R.keys(networkPricing)) {
    for (const rotation of networkPricing[avail]) {
      if (rotation.status === 'buyable') {
        typesToUse.Regular = true;
      }
      if (rotation.status === 'secured') {
        typesToUse.Secured = true;
      }
      if (rotation.status === 'upfront') {
        typesToUse.Upfront = true;
      }
    }
  }

  return ORDER_TYPE_OPTIONS.filter(type => typesToUse[type.label]).map(({ label, value }) => ({
    label,
    value: String(value)
  }));
};

/**
 * Checks which days exist in dow array.
 */
const makeDaysOfWeekMap = (dow: string[]) => {
  const daysMap = {
    M: dow.includes('M'),
    Tu: dow.includes('Tu'),
    W: dow.includes('W'),
    Th: dow.includes('Th'),
    F: dow.includes('F'),
    Sa: dow.includes('Sa'),
    Su: dow.includes('Su')
  };
  return daysMap;
};

/**
 * returns CAD or USD
 */
const currencyFormatter = (cost: number, numDecimals: number, CAD: boolean | string): string =>
  CAD ? formatCAD(cost, numDecimals) : formatMoney(cost, numDecimals);

/**
 * Check if the plan row has a pending edit of the given type.
 */
const isEdited = (row: PlanRow, editType: string) => {
  if (row.edits && R.has(editType, row.edits)) {
    return true;
  }
  return false;
};

const LENGTH_OPTIONS = LENGTHS.map(String);

/**
 * The cell dropdowns take string values; plan rows store some fields as numbers or null.
 */
const toCellValue = (value: unknown): string | null => (value == null ? null : String(value));

/**
 * If there's an edited value, use that. Otherwise, use the original value.
 */
const valueToUse = (row: PlanRow, editType: string) => {
  if (row.edits && R.has(editType, row.edits)) {
    return row.edits[editType];
  }
  return row[editType];
};

/**
 * For aggregate row  -- If there's an edited value, use that. Otherwise, use the original value.
 */
const valueToUseAggregate = (
  row: PlanRow,
  formattedRow: FormattedRow,
  editType: string,
  aggregateProp: string
) => {
  if (row) {
    if (row.edits && R.has(editType, row.edits)) {
      return row.edits[editType];
    }
    return row[editType];
  }
  return formattedRow[aggregateProp];
};

/**
 * Returns the rotation options for the given row.
 */
const getRotationOptions = (
  row: FormattedRow,
  rotations: RotationPricing[]
): {
  label: string;
  value: string;
}[] => {
  if (!rotations) {
    return [];
  }
  const currentType: (typeof TYPES)[number] = valueToUse(row, 'type');
  const currentStatus = TYPE_TO_STATUS_IN_NETWORK_PRICING[currentType];
  return rotations
    .filter(rotation => {
      if (currentStatus === 'general') {
        return rotation.status === currentStatus || rotation.status === 'buyable';
      }
      return rotation.status === currentStatus;
    })
    .map(rotation => ({
      label: rotation.rotationLabel,
      value: rotation.rotationLabel
    }));
};

/**
 * Returns the market options for the given row.
 */
const getMarketOptions = (row: FormattedRow, markets: RotationPricing[]): string[] => {
  if (!markets) {
    return [];
  }
  const currentType: (typeof TYPES)[number] = valueToUse(row, 'type');
  const currentStatus = TYPE_TO_STATUS_IN_NETWORK_PRICING[currentType];

  return markets
    .filter(rotation => {
      if (currentStatus === 'general') {
        return rotation.status === currentStatus || rotation.status === 'buyable';
      }
      return rotation.status === currentStatus;
    })
    .flatMap(({ market }) => (market === null ? [] : [market]));
};

const getAvailOptions = (networkPricing: Record<string, RotationPricing[]>) =>
  R.keys(networkPricing).map(avail => ({
    label: avail,
    value: avail
  }));

interface EditTableViewProps {
  channel: string;
  rows: PlanRow[];
  editsMap: EditsMap;
  rotationsAndPricing: RotationsAndPricing;
  nielsenEstimates: NielsenEstimates;
  /** Nielsen is fetching; the audience columns say so rather than blocking the page. */
  nielsenLoading: boolean;
  selectedDemos: Record<string, boolean>;
  selectedMeasurement: string;
  universeEstimate: number | undefined;
  campaignMetadata: CampaignMetadata;
  weeks: string[];
  selectedRows: Record<string, PlanRow>;
  setNewRows: Dispatch<SetStateAction<Record<string, PlanRow>>>;
  setEditsMap: Dispatch<SetStateAction<EditsMap>>;
  setSelectedRows: Dispatch<SetStateAction<Record<string, PlanRow>>>;
  setCreativeAllocationModalData: Dispatch<SetStateAction<PlanRow[] | undefined>>;
  /**
   * Whether a blanket traffic instruction already covers this row, in which case it needs no
   * per-line rotation. Omitted when the feature is off.
   */
  isBlanketCovered?: (row: PlanRow) => boolean;
}

export const EditTableView = ({
  channel,
  rows,
  editsMap,
  rotationsAndPricing,
  nielsenEstimates,
  nielsenLoading,
  selectedDemos,
  selectedMeasurement,
  universeEstimate,
  campaignMetadata,
  weeks,
  selectedRows,
  setNewRows,
  setEditsMap,
  setSelectedRows,
  setCreativeAllocationModalData,
  isBlanketCovered
}: EditTableViewProps) => {
  const enableCampaigns = useExperimentFlag('enableLinearCampaigns');
  const enableRadioBuying = useExperimentFlag('enableRadioBuying');
  const enableLinearBuyingUpdates = useExperimentFlag('enableAllLinearBuyingChanges');
  const enableCAD = useExperimentFlag('enableCADLinearCompanies');
  const allowLinearRotationBypass = useExperimentFlag('allowLinearRotationBypass');
  const enableUpfrontLinear = useExperimentFlag('enableUpfrontLinear');
  const enablePastLinearBuyingEdits = useExperimentFlag('enablePastLinearBuyingEdits');
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const allowPastLinearEdits = isAdmin || Boolean(enablePastLinearBuyingEdits);
  const isPastLinearLocked =
    weeks[0] < CURRENT_WEEK_START && channel === 'linear' && !allowPastLinearEdits;

  const [focusedCell, setFocusedCell] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const isMultiWeek = useMemo(() => isMultiDatePicker(weeks), [weeks]);

  // The three columns derived from the Nielsen estimates. Matched on the header label because the
  // column list is filtered at render time, so a positional index isn't stable.
  const audienceColumnLabels = useMemo(
    () =>
      new Set(
        enableLinearBuyingUpdates
          ? ['Secondary Impressions', 'Secondary CPM', 'Secondary TRP']
          : ['Impressions', 'CPM', 'TRP']
      ),
    [enableLinearBuyingUpdates]
  );

  const editRow = useCallback(
    (row: PlanRow, edits: RowEdit, key: string, isNewRow: boolean) => {
      if (isNewRow) {
        setNewRows(current => ({ ...current, [key]: { ...current[key], ...edits } }));
      } else {
        setEditsMap(current => ({
          ...current,
          [key]: { ...row, edits: { ...current[key]?.edits, ...edits } }
        }));
      }
    },
    [setEditsMap, setNewRows]
  );

  // In multi-week mode a table row aggregates one plan row per week, so an edit has to be
  // applied to each of them; otherwise it applies to the single row.
  const applyEdits = useCallback(
    (row: FormattedRow, edits: RowEdit, key: string, isNewRow: boolean) => {
      if (isMultiWeek) {
        for (const individualRow of row.rows) {
          editRow(individualRow, edits, individualRow.key, Boolean(individualRow.isNewRow));
        }
      } else {
        editRow(row, edits, key, isNewRow);
      }
    },
    [editRow, isMultiWeek]
  );

  const selectRow = useCallback(
    (row: FormattedRow) => {
      const { key } = row;
      if (R.has(key, selectedRows)) {
        setSelectedRows(current => R.omit([key], current));
      } else {
        setSelectedRows(current => ({ ...current, [key]: row }));
      }
    },
    [selectedRows, setSelectedRows]
  );

  const formattedRows: FormattedRow[] = useMemo(
    () =>
      (rows || []).map((row: PlanRow) => {
        let rowToUse = row;
        if (editsMap[row.key]) {
          rowToUse = editsMap[row.key];
        }
        // impressions for buys if not upfront type but in linear buying
        const formattedType: string = TYPES_TO_NAMES[`${rowToUse.type}`];
        const totalSpend = rowToUse.count * rowToUse.cost;
        const overriddenImpressions = row.impressions;

        // Calculate CPM for ordered impressons if it's an upfront buy
        let orderedCpm = 0;
        if (isUpfront(rowToUse.type) && rowToUse.orderedImpressions && enableLinearBuyingUpdates) {
          orderedCpm = (totalSpend / rowToUse.orderedImpressions) * 1000;
          if (orderedCpm === Infinity) {
            orderedCpm = 0;
          }
        }

        // For all buying types rowToUse.edits.impressions is always updated if the impressions or secondary impressions column is changed
        // If impressionOverrides (secondary impressions on site) is changed for upfront type, rowToUse.edits.impressions and rowToUse.edits.impressionOverrides both are updated
        // Impressions is always the number displayed in impressions or secondary impressions on site so impressions should match the impressions in impressionsOverride object for upfront types
        let impressions;
        let cpm;
        let trp;
        // if type is upfront && impressions have been saved to the database
        if (
          isUpfront(rowToUse.type) &&
          overriddenImpressions !== 0 &&
          !R.isNil(overriddenImpressions)
        ) {
          impressions = overriddenImpressions || 0;
          cpm = (totalSpend / overriddenImpressions) * 1000 || 0;
          trp = 0; // TODO: Wait for confirmation from Michelle about using Impression Overrides to calculate TRP
        } else {
          // upfront buys without impressions or all other buy types will display nielsens
          // unless there is an overriden impressions for non-upfront buy types saved in database
          ({ impressions, cpm, trp } = calculateAudienceInfo({
            row: { ...rowToUse, ...rowToUse.edits },
            nielsenEstimates,
            universeEstimate
          }));
        }
        const data: FormattedRow = {
          ...rowToUse,
          formattedType,
          totalSpend,
          impressions: overriddenImpressions || impressions,
          // Denominator used to compute `cpm` above. Differs from `impressions` for non-Upfront
          // rows with a saved override (column shows the override; CPM uses the Nielsen estimate).
          secondaryImpressions: impressions,
          cpm,
          trp,
          orderedCpm, // TODO: Wait for confirmation from Michelle about including Ordered TRP as well
          rows: []
        };
        return data;
      }),
    [rows, editsMap, enableLinearBuyingUpdates, nielsenEstimates, universeEstimate]
  );

  const totalsRow = useMemo(() => {
    if (R.isEmpty(formattedRows)) {
      return undefined;
    }
    const aggSpotsTotals: totalsRowAggSpots = {};
    for (const week of weeks) {
      aggSpotsTotals[weekFormatter(week)] = 0;
    }
    let totalCount = 0;
    let totalSpend = 0;
    let totalImpressions = 0;
    let totalSecondaryImpressions = 0;
    let totalTRP = 0;
    for (let row of formattedRows) {
      if (editsMap[row.key]) {
        row = { ...row, ...editsMap[row.key].edits };
      }
      const spend = row.count * row.cost;
      aggSpotsTotals[weekFormatter(row.week)] += row.count || 0;
      totalCount += row.count;
      totalSpend += spend;
      totalImpressions += row.impressions || 0;
      totalSecondaryImpressions += row.secondaryImpressions || 0;
      totalTRP += row.trp || 0;
    }

    let totalCPM = (totalSpend / totalSecondaryImpressions) * 1000;
    if (totalCPM === Infinity) {
      totalCPM = 0;
    }

    const total = {
      count: formatNumberAsInt(totalCount),
      totalSpend: currencyFormatter(totalSpend, 0, enableCAD),
      impressions: formatNumberAsInt(totalImpressions),
      cpm: currencyFormatter(totalCPM, 2, enableCAD),
      trp: formatNumber(totalTRP, 1),
      ...aggSpotsTotals
    };
    return total;
  }, [formattedRows, editsMap, weeks, enableCAD]);

  // Keyed by the composite aggregation key, not indexed — an array accumulator here silently
  // produced a non-iterable value that `for...of` choked on downstream.
  const aggregatedRows: Record<string, FormattedRow> = useMemo(
    () =>
      formattedRows.reduce((acc, row) => {
        // check dates and return
        if (!isMultiWeek || R.isEmpty(formattedRows)) {
          return acc;
        }
        const {
          company,
          channel: rowChannel,
          network,
          avail,
          rotation,
          length,
          dow,
          daypart_start,
          daypart_end,
          cost,
          type,
          campaign_id,
          media_classification,
          market,
          orderedImpressionsDemo,
          orderedImpressionsMeasurement
        } = row;
        let key: string;
        if (row?.edits) {
          // `??`, not `||`: an edit can legitimately be falsy — Regular is type 0, and a spot can
          // be zeroed out — and `||` would silently key those rows off their pre-edit values.
          key = `${network}_${row.edits?.avail ?? row.avail}_${row.edits?.length ?? length}_${
            row.edits?.dow ?? row.dow
          }_${row.edits?.daypart_start ?? row.daypart_start}_${
            row.edits?.daypart_end ?? row.daypart_end
          }_${row.edits?.cost ?? row.cost}_${row.edits?.rotation ?? row.rotation}_${
            row.edits?.type ?? row.type
          }_${row.edits?.campaign_id ?? row.campaign_id}_${
            row.edits?.media_classification ?? row.media_classification
          }_${market}`;
        } else {
          key = `${network}_${avail}_${length}_${dow}_${daypart_start}_${daypart_end}_${cost}_${rotation}_${type}_${campaign_id}_${media_classification}_${market}`;
        }
        if (!acc[key]) {
          acc[key] = {
            company,
            channel: rowChannel,
            network,
            avail,
            length,
            dow,
            daypart_start,
            daypart_end,
            cost,
            rotation,
            type,
            campaign_id,
            media_classification,
            market,
            key,
            notes: '',
            cpm: 0,
            count: 0,
            impressions: 0,
            secondaryImpressions: 0,
            totalSpend: 0,
            trp: 0,
            creatives: null,
            rows: [],
            edits: false,
            isNewRow: false,
            orderedImpressions: 0,
            orderedImpressionsDemo,
            orderedImpressionsMeasurement,
            orderedCpm: 0
          };
        }
        acc[key].avail = valueToUse(row, 'avail');
        acc[key].market = row.market;
        acc[key].length = valueToUse(row, 'length');
        acc[key].dow = valueToUse(row, 'dow');
        acc[key].daypart_start = valueToUse(row, 'daypart_start');
        acc[key].daypart_end = valueToUse(row, 'daypart_end');
        acc[key].rotation = valueToUse(row, 'rotation');
        acc[key].cost = valueToUse(row, 'cost');
        acc[key].type = valueToUse(row, 'type');
        acc[key].campaign_id = valueToUse(row, 'campaign_id');
        acc[key].media_classification = valueToUse(row, 'media_classification');
        acc[key].creatives = valueToUse(row, 'creatives');
        // row.impressions already takes edits into account
        acc[key].impressions += row.impressions || 0;
        acc[key].secondaryImpressions += row.secondaryImpressions || 0;
        acc[key].count += valueToUse(row, 'count') || 0;
        acc[key].totalSpend = acc[key].count * acc[key].cost;
        acc[key].cpm = (acc[key].totalSpend / acc[key].secondaryImpressions) * 1000;
        if (acc[key].cpm === Infinity) {
          acc[key].cpm = 0;
        }
        acc[key].trp += row.trp || 0;
        acc[key].orderedImpressions += valueToUse(row, 'orderedImpressions') || 0;
        acc[key].orderedCpm = (acc[key].totalSpend / acc[key].orderedImpressions) * 1000;
        if (acc[key].orderedCpm === Infinity) {
          acc[key].orderedCpm = 0;
        }
        acc[key].rowId = row.key;
        acc[key].rows = R.concat(acc[key].rows, [row]);
        acc[key][weekFormatter(row.week)] = row.count || 0;
        if (row.edits?.notes) {
          acc[key].notes += `${Dfns.format('M/d/yyyy', Dfns.parseISO(row.week))}: ${
            row.edits?.notes
          } `;
        } else if (row.notes) {
          acc[key].notes += `${Dfns.format('M/d/yyyy', Dfns.parseISO(row.week))}: ${row.notes} `;
        }
        return acc;
      }, {}),
    [formattedRows, isMultiWeek]
  );
  const editedAggregatedRows: Record<string, FormattedRow> = useMemo(() => {
    const editedAggRows: Record<string, FormattedRow> = { ...aggregatedRows };
    for (const key of Object.keys(aggregatedRows)) {
      const editedRow: FormattedRow = { ...aggregatedRows[key] };
      // Weeks the aggregate has no rows for still need a zeroed spots column.
      for (const week of weeks) {
        const spotsLabel = weekFormatter(week);
        if (!R.has(spotsLabel, editedRow)) {
          editedRow[spotsLabel] = 0;
        }
      }
      editedAggRows[key] = editedRow;
    }
    return editedAggRows;
  }, [aggregatedRows, weeks]);

  /**
   * Whether a blanket instruction covers the row the grid is drawing. In the multi-week view that
   * row is an aggregate standing for several weeks, and the aggregation never gives it a `week` of
   * its own — asking `isBlanketCovered` directly would compare `undefined` against the
   * instruction's start week and always answer false. Coverage is per week, so ask the underlying
   * rows and only call the aggregate covered when the instruction covers every one of them.
   */
  const isRowBlanketCovered = (row: FormattedRow): boolean =>
    areAllRowsCovered(isMultiWeek ? row.rows : [row], isBlanketCovered);

  let tableHeaders = [
    {
      label: '',
      name: 'id',
      width: 40,
      nonInteractive: true,
      renderer: (row: FormattedRow) => (
        <Checkbox
          className='checkbox'
          checked={R.has(row.key, selectedRows)}
          onChange={() => selectRow(row)}
        />
      )
    },
    {
      label: 'Network',
      name: 'network',
      renderer: (row: FormattedRow) => (
        <div className={cn({ 'new-row': isNewRowInAggregate(row.rows) || row.isNewRow })}>
          {row.network}
        </div>
      )
    },
    {
      label: 'Avail',
      name: 'avail',
      width: 85,
      renderer: (row: FormattedRow, i: number) => {
        const { network, avail, key, isNewRow } = row;
        const networkPricing = R.pathOr({}, [network], rotationsAndPricing);
        const availOptions = getAvailOptions(networkPricing);
        return (
          <>
            {(focusedCell === `avail_${i}` && isNewRow) ||
            (focusedCell === `avail_${i}` && isNewRowInAggregate(row.rows)) ? (
              <CellDropdown
                className={cn({
                  'new-row': isNewRowInAggregate(row.rows) || isNewRow
                })}
                disabled={isNewRowInAggregate(row.rows) ? false : isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                value={toCellValue(valueToUse(row, 'avail'))}
                options={availOptions}
                onChange={selection => {
                  const newAvail = AVAILS.find(availOption => availOption === selection);
                  if (!newAvail) {
                    return;
                  }

                  /*
                  When changing avail, need to also change:
                    - the rotation because some rotations only exist for a specific avail
                    - the market, cost, times, and dow based on the new rotation
                  */
                  let newCost = 0;
                  let newRotation = 'No rotation selected';
                  let newStartTime = '06:00';
                  let newEndTime = '06:00';
                  let newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                  let newMarket: string | null = null;
                  // Placeholder values come from the network's first rotation for this avail.
                  const [firstRotation] = networkPricing[newAvail];
                  if (firstRotation) {
                    newCost = firstRotation.cost30s;
                    newRotation = firstRotation.rotationLabel;
                    newStartTime = time24hr(firstRotation.daypartBegin);
                    newEndTime = time24hr(firstRotation.daypartEnd);
                    newDow = firstRotation.daysOfWeek;
                    newMarket = firstRotation.market;
                  }

                  const availEdits = {
                    avail: newAvail,
                    cost: newCost,
                    rotation: newRotation,
                    daypart_start: newStartTime,
                    daypart_end: newEndTime,
                    dow: newDow,
                    market: newMarket
                  };
                  applyEdits(row, availEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`avail_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isEdited(row, 'avail') && <span className='old-value'>{avail}</span>}
                <span>{valueToUse(row, 'avail')}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Rotation',
      name: 'rotation',
      flex: 1,
      minFlexWidth: 300,
      renderer: (row: FormattedRow, i: number) => {
        const { network, avail, rotation, type, length, key, isNewRow } = row;
        const networkPricing = R.pathOr({}, [network], rotationsAndPricing);
        let rotationOptions = getRotationOptions(row, networkPricing[avail]);
        if (allowLinearRotationBypass) {
          rotationOptions = R.concat([{ label: 'Override', value: 'Override' }], rotationOptions);
        }

        if (focusedCell === `rotation_${i}_rotation_override`) {
          return (
            <OverrideRotationModal
              addOverrideRotation={overrideRotation => {
                const newCost = 0;
                const newStartTime = '06:00';
                const newEndTime = '06:00';
                const newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                const newMarket: string | null = null;
                const rotationEdits = {
                  rotation: overrideRotation,
                  isOverrideRotation: true,
                  cost: newCost,
                  daypart_start: newStartTime,
                  daypart_end: newEndTime,
                  dow: newDow,
                  market: newMarket
                };
                applyEdits(row, rotationEdits, key, Boolean(isNewRow));
                setFocusedCell('');
              }}
              onClose={() => setFocusedCell('')}
            />
          );
        }
        if (focusedCell === `rotation_${i}`) {
          return (
            <CellDropdown
              disabled={isPastLinearLocked}
              onBlur={() => setFocusedCell('')}
              value={toCellValue(valueToUse(row, 'rotation'))}
              options={rotationOptions}
              onChange={selection => {
                if (!selection) {
                  return;
                }
                if (selection === 'Override') {
                  setFocusedCell(`rotation_${i}_rotation_override`);
                } else {
                  const newRotation = selection;
                  /*
                    When changing rotation, need to also change:
                      - the cost based on the new rotation (need to account for length too)
                      - the times based on the new rotation
                      - days of the week based on the new rotation
                      - market based on the new rotation
                    */
                  let newCost = 0;
                  let newStartTime = '06:00';
                  let newEndTime = '06:00';
                  let newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                  let newMarket: string | null = null;
                  const formattedTypeForNetworkPricing = TYPE_TO_STATUS_IN_NETWORK_PRICING[type];
                  for (const networkRotation of networkPricing[avail]) {
                    if (
                      newRotation === networkRotation.rotationLabel &&
                      shouldUseRotation(formattedTypeForNetworkPricing, networkRotation.status)
                    ) {
                      newCost = networkRotation.cost30s;
                      newStartTime = time24hr(networkRotation.daypartBegin);
                      newEndTime = time24hr(networkRotation.daypartEnd);
                      newDow = networkRotation.daysOfWeek;
                      newMarket = networkRotation.market;
                    }
                  }
                  if (length === 15) {
                    newCost /= 2;
                  }
                  if (length === 60) {
                    newCost *= 2;
                  }

                  const rotationEdits = {
                    rotation: newRotation,
                    cost: newCost,
                    daypart_start: newStartTime,
                    daypart_end: newEndTime,
                    dow: newDow,
                    market: newMarket
                  };
                  applyEdits(row, rotationEdits, key, Boolean(isNewRow));
                }
              }}
            />
          );
        }
        return (
          <InteractiveDiv
            onClick={() => {
              setFocusedCell(`rotation_${i}`);
            }}
            className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
          >
            {isMultiWeek
              ? isMultiWeekEdited(row.rows, 'rotation') && (
                  <span className='old-value'>{findOldMetricString(row.rows, 'rotation')}</span>
                )
              : isEdited(row, 'rotation') && <span className='old-value'>{rotation}</span>}
            <span>{valueToUse(row, 'rotation')}</span>
          </InteractiveDiv>
        );
      }
    },
    {
      label: 'Market',
      name: 'market',
      flex: 1,
      minFlexWidth: 150,
      renderer: (row: FormattedRow, i: number) => {
        const { network, avail, market, type, length, key, isNewRow } = row;
        const networkPricing = R.pathOr({}, [network], rotationsAndPricing);
        const marketOptions = getMarketOptions(row, networkPricing[avail]);
        return (
          <>
            {focusedCell === `market_${i}` ? (
              <CellDropdown
                disabled={isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                value={toCellValue(valueToUse(row, 'market'))}
                options={marketOptions}
                onChange={selection => {
                  if (!selection) {
                    return;
                  }
                  const newMarket = selection;
                  /*
                  When changing market, need to also change:
                    - the rotation because some rotations only exist for a specific market
                    - the cost, times, and dow based on the new rotation
                  */
                  let newCost = 0;
                  let newStartTime = '06:00';
                  let newEndTime = '06:00';
                  let newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                  let newRotation = 'No rotation selected';

                  const formattedTypeForNetworkPricing = TYPE_TO_STATUS_IN_NETWORK_PRICING[type];

                  for (const rotation of networkPricing[avail]) {
                    if (
                      newMarket === rotation.market &&
                      shouldUseRotation(formattedTypeForNetworkPricing, rotation.status)
                    ) {
                      newCost = rotation.cost30s;
                      newStartTime = time24hr(rotation.daypartBegin);
                      newEndTime = time24hr(rotation.daypartEnd);
                      newDow = rotation.daysOfWeek;
                      newRotation = rotation.rotationLabel;
                    }
                  }

                  if (length === 15) {
                    newCost /= 2;
                  }
                  if (length === 60) {
                    newCost *= 2;
                  }

                  const rotationEdits = {
                    rotation: newRotation,
                    cost: newCost,
                    daypart_start: newStartTime,
                    daypart_end: newEndTime,
                    dow: newDow,
                    market: newMarket
                  };

                  applyEdits(row, rotationEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`market_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'market') && (
                      <span className='old-value'>{findOldMetricString(row.rows, 'market')}</span>
                    )
                  : isEdited(row, 'market') && <span className='old-value'>{market}</span>}
                <span>{valueToUse(row, 'market')}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Start Time',
      name: 'daypart_start',
      width: 150,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, daypart_start } = row;
        return (
          <>
            {focusedCell === `startTime_${i}` ? (
              <SelfFocusingInput
                disabled={isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                type='time'
                value={time24hr(valueToUse(row, 'daypart_start')) || ''}
                onChange={e => {
                  const newStartTime = e.target.value || '00:00';

                  const daypartStartEdit = {
                    daypart_start: newStartTime
                  };

                  applyEdits(row, daypartStartEdit, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`startTime_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'daypart_start') && (
                      <span className='old-value'>
                        {convert24hrTo12hr(findOldMetricString(row.rows, 'daypart_start'))}
                      </span>
                    )
                  : isEdited(row, 'daypart_start') && (
                      <span className='old-value'>{convert24hrTo12hr(daypart_start)}</span>
                    )}
                <span>{convert24hrTo12hr(valueToUse(row, 'daypart_start'))}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'End Time',
      name: 'daypart_end',
      width: 150,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, daypart_end } = row;
        return (
          <>
            {focusedCell === `endTime_${i}` ? (
              <SelfFocusingInput
                disabled={isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                type='time'
                value={time24hr(valueToUse(row, 'daypart_end')) || ''}
                onChange={e => {
                  const newEndTime = e.target.value || '00:00';

                  const daypartEndEdit = {
                    daypart_end: newEndTime
                  };

                  applyEdits(row, daypartEndEdit, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`endTime_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'daypart_end') && (
                      <span className='old-value'>
                        {convert24hrTo12hr(findOldMetricString(row.rows, 'daypart_end'))}
                      </span>
                    )
                  : isEdited(row, 'daypart_end') && (
                      <span className='old-value'>{convert24hrTo12hr(daypart_end)}</span>
                    )}
                <span>{convert24hrTo12hr(valueToUse(row, 'daypart_end'))}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Days',
      name: 'dow',
      flex: 1,
      minFlexWidth: 250,
      renderer: (row: FormattedRow) => {
        const { key, isNewRow } = row;
        const daysOfWeek = makeDaysOfWeekMap(valueToUse(row, 'dow'));
        return (
          <ButtonGroup className='days-of-week'>
            {DAYS_OF_WEEK.map(day => (
              <Button
                disabled={isPastLinearLocked}
                key={day}
                appearance={daysOfWeek[day] ? Appearance.FILLED : Appearance.OUTLINED}
                onClick={() => {
                  daysOfWeek[day] = !daysOfWeek[day];
                  let newDow: string[] = DAYS_OF_WEEK.filter(dayOfWeek => daysOfWeek[dayOfWeek]);

                  if (!newDow.length) {
                    newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                  }

                  const dayOfWeekEdits = {
                    dow: newDow
                  };
                  applyEdits(row, dayOfWeekEdits, key, Boolean(isNewRow));
                }}
              >
                {day}
              </Button>
            ))}
          </ButtonGroup>
        );
      }
    },
    {
      label: 'Type',
      name: 'type',
      width: 100,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, type, avail, network, length } = row;
        const networkPricing = R.pathOr({}, [network], rotationsAndPricing);
        const typeOptions = getTypeOptions(networkPricing, enableUpfrontLinear);
        return (
          <>
            {(focusedCell === `type_${i}` && isNewRow) ||
            (focusedCell === `type_${i}` && isNewRowInAggregate(row.rows)) ? (
              <CellDropdown
                disabled={isNewRowInAggregate(row.rows) ? false : isPastLinearLocked}
                options={typeOptions}
                onBlur={() => setFocusedCell('')}
                value={toCellValue(valueToUse(row, 'type'))}
                onChange={selection => {
                  const newType = TYPES.find(typeOption => String(typeOption) === selection);
                  if (newType === undefined) {
                    return;
                  }

                  /*
                  When changing type, need to also change:
                    - the rotation because some rotations only exist for a specific type
                    - the market, cost, times, and dow based on the new rotation
                  */
                  let newCost = 0;
                  let newRotation = 'No rotation selected';
                  let newStartTime = '06:00';
                  let newEndTime = '06:00';
                  let newDow = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];
                  let newMarket: string | null = null;

                  const formattedTypeForNetworkPricing = TYPE_TO_STATUS_IN_NETWORK_PRICING[newType];

                  for (const rotation of networkPricing[avail]) {
                    if (shouldUseRotation(formattedTypeForNetworkPricing, rotation.status)) {
                      // We store rates for 30s lengths only. Need to convert the rate to the correct length.
                      newCost = rotation.cost30s * (length / 30);
                      newRotation = rotation.rotationLabel;
                      newStartTime = time24hr(rotation.daypartBegin);
                      newEndTime = time24hr(rotation.daypartEnd);
                      newDow = rotation.daysOfWeek;
                      newMarket = rotation.market;
                      break;
                    }
                  }

                  const typeEdits = {
                    type: newType,
                    cost: newCost,
                    rotation: newRotation,
                    daypart_start: newStartTime,
                    daypart_end: newEndTime,
                    dow: newDow,
                    market: newMarket
                  };

                  applyEdits(row, typeEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`type_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'type') && (
                      <span className='old-value'>{findOldMetricString(row.rows, 'type')}</span>
                    )
                  : isEdited(row, 'type') && (
                      <span className='old-value'>{TYPES_TO_NAMES[`${type}`]}</span>
                    )}
                <span>{TYPES_TO_NAMES[`${valueToUse(row, 'type')}`]}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Length',
      name: 'length',
      width: 85,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, length, network, avail } = row;
        const networkPricing = R.pathOr({}, [network], rotationsAndPricing);
        return (
          <>
            {focusedCell === `length_${i}` ? (
              <CellDropdown
                disabled={isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                options={LENGTH_OPTIONS}
                value={toCellValue(valueToUse(row, 'length'))}
                onChange={selection => {
                  const newLength = LENGTHS.find(
                    lengthOption => String(lengthOption) === selection
                  );
                  if (newLength === undefined) {
                    return;
                  }

                  let newCost = 0;

                  for (const rotation of networkPricing[avail]) {
                    if (
                      row.rotation === rotation.rotationLabel &&
                      TYPE_TO_STATUS_IN_NETWORK_PRICING[row.type] === rotation.status
                    ) {
                      newCost = rotation.cost30s;
                    }
                  }

                  // We store rates for 30s lengths only. Need to convert the rate to the correct length.
                  newCost *= newLength / 30;

                  const lengthEdits = {
                    length: newLength,
                    cost: Math.round(newCost)
                  };
                  applyEdits(row, lengthEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`length_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'length') && (
                      <span className='old-value'>{findOldMetricString(row.rows, 'length')}</span>
                    )
                  : isEdited(row, 'length') && <span className='old-value'>{length}</span>}
                <span>{valueToUse(row, 'length')}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Rate',
      name: 'cost',
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, cost } = row;
        return (
          <>
            {focusedCell === `rate_${i}` ? (
              <SelfFocusingInput
                min='0'
                disabled={isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                type='number'
                value={valueToUse(row, 'cost')}
                onChange={e => {
                  let newCost = parseInt(e.target.value, 10) || 0;

                  if (newCost < 0) {
                    newCost = Math.abs(newCost);
                  }

                  const costEdits = {
                    cost: newCost
                  };
                  applyEdits(row, costEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`rate_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'cost') && (
                      <span className='old-value'>
                        {currencyFormatter(findOldMetricNumber(row.rows, 'cost'), 0, enableCAD)}
                      </span>
                    )
                  : isEdited(row, 'cost') && (
                      <span className='old-value'>{currencyFormatter(cost, 0, enableCAD)}</span>
                    )}
                <span>{currencyFormatter(valueToUse(row, 'cost'), 0, enableCAD)}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: isMultiWeek ? 'Total Spots' : 'Spots',
      name: 'count',
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, count } = row;
        return (
          <>
            {focusedCell === `spots_${i}` ? (
              <SelfFocusingInput
                min='0'
                disabled={isPastLinearLocked || isMultiWeek}
                onBlur={() => setFocusedCell('')}
                type='number'
                value={valueToUse(row, 'count')}
                onChange={e => {
                  let newCount = parseInt(e.target.value, 10) || 0;

                  if (newCount < 0) {
                    newCount = Math.abs(newCount);
                  }

                  const countEdits = {
                    count: newCount
                  };

                  editRow(row, countEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`spots_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isEdited(row, 'count') && <span className='old-value'>{count}</span>}
                <span>{valueToUse(row, 'count')}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: isMultiWeek ? 'Total Spend' : 'Spend',
      name: 'totalSpend',
      renderer: (row: FormattedRow) => {
        const { isNewRow } = row;
        return (
          <div className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}>
            {isMultiWeek
              ? currencyFormatter(valueToUse(row, 'totalSpend'), 0, enableCAD)
              : currencyFormatter(valueToUse(row, 'cost') * valueToUse(row, 'count'), 0, enableCAD)}
          </div>
        );
      }
    },

    {
      label: 'Media Classification',
      name: 'media_classification',
      width: 150,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, media_classification } = row;
        return (
          <>
            {focusedCell === `mediaClassification_${i}` ? (
              <CellDropdown
                disabled={isPastLinearLocked}
                options={
                  isUpfront(row.type)
                    ? MEDIA_CLASSIFICATION_OPTIONS
                    : [{ label: 'High Profile', value: 'High Profile' }]
                }
                onBlur={() => setFocusedCell('')}
                clearable
                value={toCellValue(valueToUse(row, 'media_classification'))}
                onChange={selection => {
                  // A cleared cell saves null. PlanRow allows null here, but bpm-types RowEdit
                  // types media_classification as string only.
                  const medEdits = { media_classification: selection } as RowEdit;
                  applyEdits(row, medEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`mediaClassification_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'media_classification') && (
                      <span className='old-value'>
                        {findOldMetricString(row.rows, 'media_classification')}
                      </span>
                    )
                  : isEdited(row, 'media_classification') && (
                      <span className='old-value'>{media_classification}</span>
                    )}
                <span>{valueToUse(row, 'media_classification') || '-'}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Campaign',
      name: 'campaign',
      flex: 1,
      minFlexWidth: 150,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow } = row;
        const { options, idToName } = campaignMetadata;
        return (
          <>
            {focusedCell === `campaign_${i}` && isNewRow ? (
              <CellDropdown
                disabled={isPastLinearLocked}
                options={options.map(({ label, value }) => ({ label, value: String(value) }))}
                onBlur={() => setFocusedCell('')}
                value={toCellValue(valueToUse(row, 'campaign_id'))}
                onChange={selection => {
                  if (!selection) {
                    return;
                  }
                  const newCampaign = Number(selection);

                  const campaignEdits = {
                    campaign_id: newCampaign
                  };
                  applyEdits(row, campaignEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`campaign_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                <span>{idToName[valueToUse(row, 'campaign_id')] || '-'}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Ordered Impressions',
      name: 'orderedImpressions',
      flex: 1,
      minFlexWidth: 200,
      renderer: (row: FormattedRow, i: number) => {
        const { type, isNewRow } = row;
        return (
          <>
            {focusedCell === `ordered_impressions_${i}` ? (
              <AddOrderedImpressionsModal
                row={row}
                editRow={editRow}
                onClose={() => setFocusedCell('')}
              />
            ) : (
              <InteractiveDiv
                onClick={() => {
                  if (isUpfront(type) && !isMultiWeek) {
                    setFocusedCell(`ordered_impressions_${i}`);
                  }
                }}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isUpfront(type) ? (
                  <div className='stacked-cell'>
                    <div className='top-content'>
                      {valueToUse(row, 'orderedImpressions') || '-'}
                    </div>
                    <div className='middle-content'>
                      {PRETTY_DEMO_NAMES[valueToUse(row, 'orderedImpressionsDemo')]}
                    </div>
                    <div className='bottom-content'>
                      {
                        ABBREVIATED_MEAUREMENT_NAMES[
                          valueToUse(row, 'orderedImpressionsMeasurement')
                        ]
                      }
                    </div>
                  </div>
                ) : (
                  <div>-</div>
                )}
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Ordered CPM',
      name: 'orderedCpm',
      width: 150,
      renderer: (row: FormattedRow) =>
        row.orderedCpm ? currencyFormatter(row.orderedCpm, 2, enableCAD) : '-'
    },
    {
      label: enableLinearBuyingUpdates ? 'Secondary Impressions' : 'Impressions',
      name: 'impressions',
      flex: 1,
      minFlexWidth: enableLinearBuyingUpdates ? 200 : 150,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, impressions, impressionOverrides } = row;
        return (
          <>
            {focusedCell === `impressions_${i}` ? (
              <SelfFocusingInput
                min='0'
                disabled={isPastLinearLocked || isMultiWeek}
                onBlur={() => setFocusedCell('')}
                type='number'
                value={valueToUse(row, 'impressions')}
                onChange={e => {
                  let newImps = parseInt(e.target.value, 10) || 0;

                  if (newImps < 0) {
                    newImps = Math.abs(newImps);
                  }

                  const impsEdits = {
                    impressions: newImps
                  };
                  // For all buying types rowToUse.edits.impressions is always updated if the impressions or secondary impressions column is changed
                  // If impressionOverrides (secondary impressions on site) is changed for upfront type, rowToUse.edits.impressions and rowToUse.edits.impressionOverrides both are updated
                  // Impressions is always the number displayed in impressions or secondary impressions on site so impressions should match the impressions in impressionsOverride object for upfront types
                  editRow(row, impsEdits, key, Boolean(isNewRow));
                  if (isUpfront(row.type)) {
                    // If the row is new then the edits get saved to impressionOverrides directly
                    const currentImpEdits = isNewRow
                      ? impressionOverrides || {}
                      : editsMap[key]?.edits?.impressionOverrides || {};
                    const selectedDemo = Object.keys(selectedDemos)[0];
                    const newImpEdits = {};
                    // if change is > 0 then create the object to deepmerge
                    if (newImps !== 0) {
                      newImpEdits[selectedDemo] = {};
                      newImpEdits[selectedDemo][selectedMeasurement] = newImps;
                    }
                    // Merge the new impression override edits with the current impression overrides
                    let inputImpEdits = deepMergeLinearImpressionOverrides(
                      currentImpEdits,
                      newImpEdits
                    );
                    // if change is is 0 then create an empty object to save into edits to indicate to backend that
                    // the impression overrides should be deleted
                    if (newImps === 0) {
                      inputImpEdits = {};
                    }

                    editRow(row, { impressionOverrides: inputImpEdits }, key, Boolean(isNewRow));
                  }
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => {
                  if (!isMultiWeek) {
                    setFocusedCell(`impressions_${i}`);
                  }
                }}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isEdited(row, 'impressions') && <span className='old-value'>{impressions}</span>}
                <span>{formatNumberAsInt(valueToUse(row, 'impressions')) || '-'}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: enableLinearBuyingUpdates ? 'Secondary CPM' : 'CPM',
      name: 'cpm',
      width: enableLinearBuyingUpdates ? 150 : 85,
      renderer: (row: FormattedRow) => (row.cpm ? currencyFormatter(row.cpm, 2, enableCAD) : '-')
    },
    {
      label: enableLinearBuyingUpdates ? 'Secondary TRP' : 'TRP',
      name: 'trp',
      width: enableLinearBuyingUpdates ? 150 : 85,
      renderer: (row: FormattedRow) => (row.trp ? formatNumber(row.trp, 1) : '-')
    },
    {
      label: 'Notes',
      name: 'notes',
      flex: 1,
      minFlexWidth: 300,
      renderer: (row: FormattedRow, i: number) => {
        const { key, isNewRow, notes } = row;
        return (
          <>
            {focusedCell === `notes_${i}` ? (
              <SelfFocusingInput
                disabled={isMultiWeek || isPastLinearLocked}
                onBlur={() => setFocusedCell('')}
                as='textarea'
                rows={1}
                maxLength={45}
                value={valueToUse(row, 'notes') || ''}
                onChange={e => {
                  const newNote = e.target.value;

                  const noteEdits = {
                    notes: newNote
                  };
                  editRow(row, noteEdits, key, Boolean(isNewRow));
                }}
              />
            ) : (
              <InteractiveDiv
                onClick={() => setFocusedCell(`notes_${i}`)}
                className={cn({ 'new-row': isNewRowInAggregate(row.rows) || isNewRow })}
              >
                {isMultiWeek
                  ? isMultiWeekEdited(row.rows, 'notes') && (
                      <span className='old-value'>{findOldMetricString(row.rows, 'notes')}</span>
                    )
                  : isEdited(row, 'notes') && <span className='old-value'>{notes}</span>}
                <span>{valueToUse(row, 'notes')}</span>
              </InteractiveDiv>
            )}
          </>
        );
      }
    },
    {
      label: 'Creatives',
      name: 'creatives',
      width: 85,
      renderer: (row: FormattedRow) => (
        <>
          {/* A fully covered row has no rotation of its own to set, so the editor is closed off
              here rather than opened onto a notice. A partly covered row stays editable -- its
              uncovered weeks still need one. */}
          <BlanketCoveredTooltip covered={isRowBlanketCovered(row)} rowKey={row.key}>
            <IconButton
              disabled={isRowBlanketCovered(row)}
              // Single-week rows aren't aggregated, so `rows` is empty — the row itself is the
              // week.
              onClick={() => setCreativeAllocationModalData(isMultiWeek ? row.rows : [row])}
            >
              <MdOpenInNew />
            </IconButton>
          </BlanketCoveredTooltip>
          {/* A row covered by a blanket traffic instruction draws its rotation from that
              instruction, so it isn't missing one -- warning here would flag every ICON row the
              feature is meant to take care of. */}
          {!row.creatives && !isRowBlanketCovered(row) && (
            <OverlayTrigger
              placement={Placement.LEFT_START}
              overlay={<Tooltip id={row.week}>Row missing rotation</Tooltip>}
            >
              <IconButton design={Intent.WARNING} size={Size.SM}>
                <MdWarning color='white' />
              </IconButton>
            </OverlayTrigger>
          )}
        </>
      )
    }
  ];

  const newCols: typeof tableHeaders = [];
  if (isMultiWeek) {
    for (const week of weeks) {
      newCols.push({
        label: weekFormatter(week),
        name: weekFormatter(week),
        renderer: (row: FormattedRow, i: number) => {
          const individualRow = getFormattedRow(row.rows, week) as FormattedRow;
          const count = row[weekFormatter(week)];
          return (
            <>
              {focusedCell === `${weekFormatter(week)}_${i}` ? (
                <SelfFocusingInput
                  min='0'
                  disabled={week < CURRENT_WEEK_START && !allowPastLinearEdits}
                  onBlur={() => setFocusedCell('')}
                  type='number'
                  value={valueToUseAggregate(individualRow, row, 'count', weekFormatter(week)) || 0}
                  onChange={e => {
                    let newCount = parseInt(e.target.value, 10) || 0;
                    if (newCount < 0) {
                      newCount = Math.abs(newCount);
                    }
                    const countEdits = {
                      count: newCount
                    };
                    if (individualRow) {
                      editRow(
                        individualRow,
                        countEdits,
                        individualRow.key,
                        Boolean(individualRow.isNewRow)
                      );
                    } else {
                      const newRowFromSpot = createNewRowFromSpots(newCount, row, week);
                      // add new row to newRows
                      setNewRows(current => ({ ...current, [newRowFromSpot.key]: newRowFromSpot }));
                    }
                  }}
                />
              ) : (
                <InteractiveDiv
                  onClick={() => setFocusedCell(`${weekFormatter(week)}_${i}`)}
                  className={cn({ 'new-row': isNew(row.rows, week) })}
                >
                  {(individualRow ? isEdited(individualRow, 'count') : false) && (
                    <span className='old-value'>{count}</span>
                  )}
                  <span>
                    {valueToUseAggregate(individualRow, row, 'count', weekFormatter(week)) || 0}
                  </span>
                </InteractiveDiv>
              )}
            </>
          );
        }
      });
    }
  }
  tableHeaders = [...tableHeaders.slice(0, 11), ...newCols, ...tableHeaders.slice(11)];

  if (!enableCampaigns) {
    tableHeaders = tableHeaders.filter(header => header.name !== 'campaign');
  }

  if (!enableRadioBuying) {
    tableHeaders = tableHeaders.filter(header => header.name !== 'market');
  }
  if ((enableLinearBuyingUpdates && isMultiWeek) || !enableLinearBuyingUpdates) {
    tableHeaders = tableHeaders.filter(
      header => header.name !== 'orderedImpressions' && header.name !== 'orderedCpm'
    );
  }

  const headersRenderer = useCallback(
    ({ data, columnIndex }) => {
      switch (columnIndex) {
        case 0:
          return (
            <Checkbox
              checked={selectAll}
              onChange={() => {
                if (selectAll) {
                  setSelectedRows({});
                } else {
                  const selectAllMap = {};
                  if (!R.isEmpty(editedAggregatedRows)) {
                    for (const row of Object.values(editedAggregatedRows)) {
                      selectAllMap[row.key] = row;
                    }
                  } else {
                    for (const row of formattedRows) {
                      selectAllMap[row.key] = row;
                    }
                  }
                  setSelectedRows(current => ({ ...current, ...selectAllMap }));
                }
                setSelectAll(!selectAll);
              }}
            />
          );
        default:
          if (nielsenLoading && audienceColumnLabels.has(data)) {
            return (
              <div className='loading-header'>
                {data}
                <Spinner size={12} />
              </div>
            );
          }
          return data;
      }
    },
    [
      formattedRows,
      editedAggregatedRows,
      setSelectedRows,
      selectAll,
      nielsenLoading,
      audienceColumnLabels
    ]
  );

  return (
    <div className='new-table-view'>
      <BPMTable<FormattedRowTableType>
        noRowsRenderer={renderNoRows}
        data={isMultiWeek ? Object.values(editedAggregatedRows) : formattedRows}
        totals={totalsRow}
        totalsRenderer={renderTotals}
        headers={tableHeaders}
        headersRenderer={headersRenderer}
        filterBar={false}
      />
    </div>
  );
};

interface CellDropdownProps<T extends string> {
  className?: string;
  disabled: boolean;
  clearable?: boolean;
  options: readonly DropdownOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  onBlur: () => void;
}

/**
 * Cell editor: takes focus when it mounts and calls onBlur when focus leaves it.
 */
const CellDropdown = <T extends string>({
  className,
  onBlur,
  ...dropdownProps
}: CellDropdownProps<T>) => (
  <div className={cn('self-focusing-select', className)}>
    <Dropdown
      {...dropdownProps}
      searchable
      fitMenuToContent
      variant={DropdownVariant.CELL}
      reactSelectProps={{ autoFocus: true, onBlur }}
    />
  </div>
);
